import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import noop from 'lodash/noop';

import MenuItem from '@mui/material/MenuItem';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';

import { selectAccountGroup } from 'companion-app-components/flux/account-groups/accountGroupsActions';
import { 
  isAccountGroupEnabled, 
  getAccountGroups, 
  selectSharedAccountGroup,
  getCurrentAccountGroupName,
} from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { hasUnAssignedAccounts } from 'companion-app-components/flux/accounts/accountsSelectors';
import { hasFullDatasetAccess } from 'companion-app-components/flux/account-groups/accountGroupUtils';
import { getCurrentDataset } from 'companion-app-components/flux/auth/authSelectors';
import { 
  ALL_ACCOUNT_GROUPS, 
  LS_KEY_SELECTED_ENTITY, 
  UN_ASSIGNED_ACCOUNTS,
} from 'companion-app-components/flux/account-groups/accountGroupsTypes';

import { MainNavItem } from 'containers/App/Header/MainNavItem';

type EntityNavItem = {
  key: string;
  name: string;
  id?: string;
};

interface EntitySelectNavButtonProps {
  classes: Record<string, any>;
  iconSize: number;
  canOpenSubMenu: boolean;
  closeHeader: () => void;
}

const EntitySelectNavButton: React.FC<EntitySelectNavButtonProps> = ({
  classes,
  iconSize,
  canOpenSubMenu,
  closeHeader,
}) => {
  const dispatch = useDispatch();

  const currentDataset = useSelector(getCurrentDataset);
  const isSharedDataset = Boolean(currentDataset?.shared);
  const accountGroups = useSelector(getAccountGroups);
  const sharedAccountGroups = useSelector(selectSharedAccountGroup);
  const showEntitySelection = useSelector(isAccountGroupEnabled);
  const selectedAccountGroupName: string | undefined = useSelector(getCurrentAccountGroupName);
  const containsUnassignedAccounts = useSelector(hasUnAssignedAccounts);

  const [selectedGroup, selectAccountGroupName] = useState<string | undefined>(selectedAccountGroupName);

  const groups = useMemo(
    () => (isSharedDataset ? sharedAccountGroups : accountGroups) ?? [],
    [isSharedDataset, sharedAccountGroups, accountGroups],
  );

  const hasFullAccess = useMemo(
    () => hasFullDatasetAccess(currentDataset),
    [currentDataset],
  );

  const menuItems = useMemo(() => {
    let items: EntityNavItem[] = groups.map(({ name, id }) => ({ key: name, name, id }));

    if (showEntitySelection && (!isSharedDataset || hasFullAccess)) {
      items = [{ key: ALL_ACCOUNT_GROUPS, name: 'All Entities' }, ...items];
      if (containsUnassignedAccounts) {
        items.push({ key: UN_ASSIGNED_ACCOUNTS, name: 'Unassigned Accounts' });
      }
    }
    return items;
  }, [groups, isSharedDataset, containsUnassignedAccounts, showEntitySelection, hasFullAccess]);

  useEffect(() => {
    if (!showEntitySelection) { return; }

    if (selectedAccountGroupName !== selectedGroup) {
      selectAccountGroupName(selectedAccountGroupName);
    }
  }, [selectedAccountGroupName, selectedGroup, dispatch, showEntitySelection]);

  const handleItemClick = (menuItemClicked: string, groupId?: string) => {
    closeHeader();
    selectAccountGroupName(menuItemClicked);
    dispatch(selectAccountGroup(menuItemClicked));
    if (groupId) {
      localStorage.setItem(LS_KEY_SELECTED_ENTITY, groupId);
    } else if ([ALL_ACCOUNT_GROUPS, UN_ASSIGNED_ACCOUNTS].includes(menuItemClicked)) {
      localStorage.setItem(LS_KEY_SELECTED_ENTITY, menuItemClicked);
    }
  };

  const hasEntityOptions = (isSharedDataset && !hasFullAccess) ? groups.length > 1 : groups.length > 0;

  if (!(showEntitySelection && hasEntityOptions)) {
    return null;
  }

  return (
    <MainNavItem
      disableClick
      key="nav-item-entity-select"
      nav={{
        component: null,
        nav: 'main',
        title: 'Entities',
        alt: 'contains account owners list',
        img: PeopleOutlineOutlinedIcon,
        subMenuItems: menuItems,
      }}
      selected=""
      classes={classes}
      canOpenSubMenu={canOpenSubMenu}
      iconSize={iconSize}
      closeHeader={closeHeader}
      handleTracking={noop}
      renderSubMenuItems={menuItems.map(({ key, name, id }) => (
        <MenuItem key={key} onClick={() => handleItemClick(key, id)}>
          <CheckIcon 
            fontSize="small" 
            sx={{ visibility: key === selectedGroup ? 'visible' : 'hidden', mr: 1 }} 
          />
          {name}
        </MenuItem>
      ))}
    />
  );
};

export default EntitySelectNavButton;
